import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  getAccessToken,
  getAccessTokenExpiry,
  getRefreshToken,
  logout,
  setAccessToken,
} from "./tokenStorage";
import api from "api/axios";
import { Organization, Workspace } from "types";
import toast from "react-hot-toast";
import { PERMISSIONS } from "./permissions";

export interface User {
  id: string;
  image?: string;
  name: string;
  email: string;
  is_super_admin?: boolean;
  is_admin?: boolean;
  current_workspace: Workspace;
  workspaces: Workspace[];
  current_organization: Organization;
  organizations: Organization[];
  is_owner?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  isLoading: boolean;
  user: User | null;
  fetchUser: () => Promise<User | null>;
  hasPermission: (permission: string) => boolean;
  isChatOnly: () => boolean;
  hasFullControl: () => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: any) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    getAccessToken() ? true : false
  );
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.current_workspace?.permissions) {
      return false;
    }
    const mockPermissions = [
      "CREATE_AGENT",
      "ADD_USER",
      "UPDATE_USER",
      "DELETE_USER",
      "ADD_GROUP",
      "UPDATE_GROUP",
      "DELETE_GROUP",
      "UPLOAD_DOCUMENT",
      "UPDATE_DOCUMENT",
      "DELETE_DOCUMENT",
      "CREATE_INTEGRATION",
      "UPDATE_INTEGRATION",
      "DELETE_INTEGRATION",
      "MANAGE_SUGGESTIONS",
    ];
    const permissions = [
      ...mockPermissions,
      ...user.current_workspace.permissions,
    ];
    // Map new user flags to permission constants used by the app
    if (user.is_super_admin) {
      permissions.push(PERMISSIONS.SUPER_ADMIN);
    } else if (user.current_organization.is_owner) {
      permissions.push(PERMISSIONS.ACCESS_MODEL_MANAGEMENT);
    }

    // Workspace owners can manage API keys
    if (user.current_workspace.is_owner) {
      permissions.push(PERMISSIONS.MANAGE_API_KEYS);
    }

    return permissions.includes(permission);
  };

  const isChatOnly = (): boolean => {
    return hasPermission(PERMISSIONS.CHAT_ONLY);
  };

  const hasFullControl = (): boolean => {
    return hasPermission(PERMISSIONS.FULL_CONTROL);
  };

  const tryRefresh = useCallback(async (): Promise<boolean> => {
    if (window.location.pathname.startsWith("/auth/")) {
      setIsLoading(false);
      return false;
    }

    const token = getRefreshToken();
    if (!token) {
      setIsLoading(false);
      logout();
      return false;
    }
    setIsLoading(true);

    try {
      const res = await api.post("/auth/refresh-token", {
        refresh_token: token,
      });
      setAccessToken(res.data.access_token, res.data.expires_in);
      return true;
    } catch (err) {
      logout();
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
    return false;
  }, []);

  const fetchUser = useCallback(async (): Promise<User | null> => {
    setIsLoading(true);

    try {
      const userInfoRes = await api.get("/auth/user-info");
      if (
        userInfoRes.data?.current_workspace?.permissions &&
        Array.isArray(userInfoRes.data.current_workspace.permissions)
      ) {
        // Remove "DELETE_USER" and "UPDATE_USER" for testing
        // userInfoRes.data.current_workspace.acl.permissions =
        //   userInfoRes.data.current_workspace.acl.permissions.filter(
        //     (perm: string) => perm !== "UPDATE_DOCUMENT" && perm !== "DELETE_DOCUMENT"
        //   );
      }
      setUser(userInfoRes.data);
      setIsAuthenticated(true);
      return userInfoRes.data;
    } catch (err: any) {
      console.error("Failed to fetch user info:", err);

      if (err?.response?.status === 401) {
        try {
          const isRefreshed = await tryRefresh();
          if (isRefreshed) {
            return await fetchUser();
          }
        } catch (refreshErr) {
          console.error("Token refresh failed:", refreshErr);
          setIsAuthenticated(false);
          logout();
          return null;
        }
      } else {
        // Other errors - set authenticated to false
        toast.error("Failed to fetch user info");
        setIsAuthenticated(false);
        logout();
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tryRefresh]);

  useEffect(() => {
    const checkAndRefresh = async () => {
      // Check if there's an access token first
      if (!getAccessToken()) {
        console.log("No access token found");
        setIsLoading(false);
        setIsAuthenticated(false);
        return;
      }

      const expiry = getAccessTokenExpiry();

      if (!expiry || new Date(expiry) <= new Date()) {
        console.log("getting user while refreshing token");

        const isRefreshed = await tryRefresh();
        if (isRefreshed) {
          await fetchUser();
        }
      } else {
        console.log("getting user");

        await fetchUser();
      }
    };

    checkAndRefresh();
  }, [tryRefresh, fetchUser]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        isLoading,
        user,
        fetchUser,
        hasPermission,
        isChatOnly,
        hasFullControl,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
